import { MockedResponse } from "@apollo/react-testing";
import {
  ProfileScreenDocument,
  ProfileScreenQuery,
  ProfileScreenQueryVariables,
  Persona,
  PropertyManagerHomeScreenQuery,
  PropertyManagerHomeScreenQueryVariables,
  PropertyManagerHomeScreenDocument,
  PropertyManagerHomePropertyFragment,
  PropertyScreenQuery,
  PropertyScreenQueryVariables,
  PropertyScreenPropertyFragment,
  PropertyScreenDocument,
  ServiceContractsQuery,
  ServiceContractsQueryVariables,
  ServiceType,
  ServiceContractsDocument,
  CostSchedule,
  ExpenseLineItem,
} from "./types";
import { DocumentNode } from "graphql";
import propertyData from "./state/properties.json";
import { find } from "lodash";
import {
  AnnualExpenseByCategory,
  ExpenseDefinition,
  TOTAL_BUILDING_MEASUREMENT,
} from "./propertyData/propertyMeasurements";
import { filter, flow, map } from "lodash/fp";
import { isDefined } from "./typeUtils";
import { TERMS_OF_USE_VERSION } from "./constants";
import { CostAnalysis, ServiceCost } from "./chartHelpers";

type SuccessMock<D, V> = {
  request: {
    query: DocumentNode;
    variables: V;
  };
  result: {
    data: D;
  };
};

const USERNAME = "demo";

const profileScreenMock: SuccessMock<
  ProfileScreenQuery,
  ProfileScreenQueryVariables
> = {
  request: {
    query: ProfileScreenDocument,
    variables: { username: USERNAME },
  },
  result: {
    data: {
      profile: {
        __typename: "Profile",
        agreedToTermsOfUse: true,
        termsOfUseVersion: TERMS_OF_USE_VERSION,
        companyLogo: null,
        name: "Self Guided Tour",
        photo: null,
        companyName: "DHM Properties",
        persona: Persona.PropertyManager,
        username: USERNAME,
        version: 0,
      },
    },
  },
};

type ExtendedServices = ServiceType | "Utilities" | "Other";

const SERVICES: ServiceType[] = [
  ServiceType.Elevators,
  ServiceType.Hvac,
  ServiceType.Janitorial,
  ServiceType.Landscaping,
  ServiceType.Security,
  ServiceType.Waste,
  ServiceType.Windows,
];

const EXTENDED_SERVICES: ExtendedServices[] = [
  ...SERVICES,
  "Utilities",
  "Other",
];

const serviceTypeToExpenseDef = (
  type: ExtendedServices
): ExpenseDefinition | undefined => {
  switch (type) {
    case ServiceType.Elevators:
      return AnnualExpenseByCategory["Elevators"];
    case ServiceType.Hvac:
      return AnnualExpenseByCategory["HVAC"];
    case ServiceType.Janitorial:
      return AnnualExpenseByCategory["Janitorial"];
    case ServiceType.Landscaping:
      return AnnualExpenseByCategory["Landscaping"];
    case ServiceType.Security:
      return AnnualExpenseByCategory["Security"];
    case ServiceType.Waste:
      return AnnualExpenseByCategory["Waste"];
    case ServiceType.Windows:
      return AnnualExpenseByCategory["Windows"];
    case "Utilities":
      return AnnualExpenseByCategory["Utilities"];
    case "Other":
      return AnnualExpenseByCategory["Other"];
    default:
      return undefined;
  }
};

type Property = PropertyManagerHomePropertyFragment &
  PropertyScreenPropertyFragment;

const mockDataToProperty = (
  mockProperty: Omit<typeof propertyData[0], "comparableProperties">
) => ({
  __typename: "Property" as const,
  id: mockProperty.id,
  rfps: null,
  measurements: {
    __typename: "ModelMeasurementsConnection" as const,
    items: [
      {
        __typename: "Measurements" as const,
        version: 0,
        id: `${mockProperty.id}-Measurements`,
        propertyId: mockProperty.id,
        values: [
          {
            __typename: "Measurement" as const,
            label: TOTAL_BUILDING_MEASUREMENT.label,
            value: String(mockProperty.sqft),
            unit: null,
            breakdown: null,
          },
        ],
      },
    ],
    nextToken: null,
  },
  expenses: {
    __typename: "ModelExpensesConnection" as const,
    items: [
      {
        __typename: "Expenses" as const,
        version: 0,
        date: new Date().toISOString(),
        id: `${mockProperty.id}-Expenses`,
        propertyId: mockProperty.id,
        analysis: {
          __typename: "ExpenseAnalysis" as const,
          costPercentage: mockProperty.costPercentage,
        },
        lineItems: flow(
          // Look for the expenses for each service type
          map((serviceType: ExtendedServices) => [
            serviceType,
            serviceTypeToExpenseDef(serviceType),
          ]),
          // Filter down to just the services with known expenses
          filter<[ServiceType, ExpenseDefinition]>(([, def]) => isDefined(def)),
          // Look for the cost asscociated with each service
          map<
            [ServiceType, ExpenseDefinition],
            [ExpenseDefinition, (ServiceCost & CostAnalysis) | undefined]
          >(([serviceType, def]) => [
            def,
            find(
              mockProperty.expenses,
              (_, key) => key.toLowerCase() === serviceType.toLowerCase()
            ),
          ]),
          // Find the cost asscociated with each service
          filter<[ExpenseDefinition, ServiceCost & CostAnalysis]>(([, cost]) =>
            isDefined(cost)
          ),
          // Map the expenses and costs to ExpenseLineItems for the property
          map<[ExpenseDefinition, ServiceCost & CostAnalysis], ExpenseLineItem>(
            ([expenseDef, cost]) => ({
              __typename: "ExpenseLineItem" as const,
              name: expenseDef.name,
              amount: cost.costPerSqft * mockProperty.sqft,
              // Default all details to 0 for the mock properties
              breakdown: !expenseDef.breakdown
                ? []
                : expenseDef.breakdown.map((detail) => ({
                    __typename: "ExpenseLineItemDetail" as const,
                    amount: 0,
                    name: detail.name,
                  })),
              analysis: cost.costPercentage
                ? {
                    __typename: "ExpenseAnalysis",
                    costPercentage: cost.costPercentage,
                  }
                : null,
            })
          )
        )(EXTENDED_SERVICES),
      },
    ],
    nextToken: null,
  },
});

const properties: Property[] = propertyData.map((data, index) => ({
  name: `Property ${index + 1}`,
  address: `${index + 1} Efficiency Lane`,
  city: "Raleigh",
  class: "Class A",
  photo: null,
  state: "NC",
  type: null,
  zipcode: "27606",
  ...mockDataToProperty(data),
  comparableProperties: {
    __typename: "ModelComparablePropertyConnection" as const,
    items: data.comparableProperties.map((comparable) => ({
      ...mockDataToProperty(comparable),
      __typename: "ComparableProperty" as const,
    })),
    nextToken: null,
  },
}));

export const propertyManagerScreenMock: SuccessMock<
  PropertyManagerHomeScreenQuery,
  PropertyManagerHomeScreenQueryVariables
> = {
  request: {
    query: PropertyManagerHomeScreenDocument,
    variables: {},
  },
  result: {
    data: {
      properties: {
        __typename: "ModelPropertyConnection",
        items: properties,
      },
    },
  },
};

const propertyScreenMock: (
  property: Property
) => SuccessMock<PropertyScreenQuery, PropertyScreenQueryVariables> = (
  property
) => ({
  request: {
    query: PropertyScreenDocument,
    variables: {
      id: property.id,
    },
  },
  result: {
    data: {
      property,
    },
  },
});

const findServiceForProperty = (
  property: Property,
  serviceType: ServiceType
): NonNullable<
  ServiceContractsQuery["serviceContractsForProperty"]
>["items"] => {
  const data = propertyData.find((data) => data.id === property.id);
  if (!data) throw new Error("Property does not exist");

  const measurements =
    property.measurements &&
    property.measurements.items &&
    property.measurements.items[0];
  if (!measurements) throw new Error("Property has no measurements");

  const serviceCost = find(
    data.expenses,
    (_, key) => key.toLowerCase() === serviceType.toLowerCase()
  );

  if (!serviceCost) return [];

  return [
    {
      __typename: "ServiceContract",
      id: `${property.id}-${serviceType}`,
      type: serviceType,
      measurementsId: measurements.id,
      measurements,
      costs: [
        {
          __typename: "CostLineItem",
          name: "Total",
          price: serviceCost.costPerSqft * data.sqft,
          schedule: CostSchedule.Annual,
        },
      ],
    },
  ];
};

const propertyScreenMocks = properties.map(propertyScreenMock);

const serviceContractMock: (
  property: Property,
  serviceType: ServiceType
) => SuccessMock<ServiceContractsQuery, ServiceContractsQueryVariables> = (
  property,
  serviceType
) => ({
  request: {
    query: ServiceContractsDocument,
    variables: {
      propertyId: property.id,
      serviceType,
    },
  },
  result: {
    data: {
      serviceContractsForProperty: {
        __typename: "ModelServiceContractConnection",
        items: findServiceForProperty(property, serviceType),
        nextToken: null,
      },
    },
  },
});

const serviceContractMocks = properties.flatMap((property) =>
  SERVICES.map((serviceType) => serviceContractMock(property, serviceType))
);

export const mocks: MockedResponse[] = [
  profileScreenMock,
  propertyManagerScreenMock,
  ...propertyScreenMocks,
  ...serviceContractMocks,
];
